import { getApiError } from '../../utils/errorHelper';
import React, { useState, useEffect } from 'react';
import { 
  Shield, Trash2, UserX, Building2, RefreshCw, Search, AlertTriangle,
  ChevronDown, ChevronRight, Eye, Users, FileText, ShoppingCart,
  ArrowRightLeft, Clock, User, CheckCircle, XCircle, Loader2,
  TrendingUp, IndianRupee, BarChart3, PieChart, Calendar, Tag, Percent
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import api from '../../services/api';
import { toIST } from '../../utils/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// API functions for super admin
const superAdminApi = {
  previewDeleteUser: async (userId) => {
    const response = await api.get(`/super-admin/users/${userId}/preview-delete`);
    return response.data;
  },
  deleteUser: async (userId, reason = null) => {
    const response = await api.delete(`/super-admin/users/${userId}`, {
      data: { confirm: true, reason }
    });
    return response.data;
  },
  deleteCompany: async (companyId, reason = null) => {
    const response = await api.delete(`/super-admin/companies/${companyId}?confirm=true${reason ? `&reason=${encodeURIComponent(reason)}` : ''}`);
    return response.data;
  },
  reassignCompany: async (companyId, newOwnerId, reason = null) => {
    const response = await api.post(`/super-admin/companies/${companyId}/reassign`, {
      new_owner_user_id: newOwnerId,
      reason
    });
    return response.data;
  },
  getAuditLogs: async (params = {}) => {
    const response = await api.get('/super-admin/audit-logs', { params });
    return response.data;
  },
  // Activity Logs APIs (Centralized Audit)
  getActivityLogs: async (params = {}) => {
    const response = await api.get('/super-admin/activity-logs', { params });
    return response.data;
  },
  getActivityLogsSummary: async (days = 30) => {
    const response = await api.get('/super-admin/activity-logs/summary', { params: { days } });
    return response.data;
  },
  exportActivityLogs: async (params = {}) => {
    const response = await api.get('/super-admin/activity-logs/export', { params });
    return response.data;
  },
  // File Access Logs
  getFileAccessLogs: async (params = {}) => {
    const response = await api.get('/super-admin/file-access-logs', { params });
    return response.data;
  },
  // Login Sessions
  getLoginSessions: async (params = {}) => {
    const response = await api.get('/super-admin/login-sessions', { params });
    return response.data;
  },
  getLoginSessionsSummary: async (days = 30) => {
    const response = await api.get('/super-admin/login-sessions/summary', { params: { days } });
    return response.data;
  },
  // Compliance Audit Logs (SOC 2 / ISO 27001)
  getComplianceAuditLogs: async (params = {}) => {
    const response = await api.get('/super-admin/compliance-audit-logs', { params });
    return response.data;
  },
  getComplianceAuditSummary: async (days = 30) => {
    const response = await api.get('/super-admin/compliance-audit-logs/summary', { params: { days } });
    return response.data;
  },
  getComplianceEventTypes: async () => {
    const response = await api.get('/super-admin/compliance-audit-logs/event-types');
    return response.data;
  },
  exportComplianceAuditLogs: async (params = {}) => {
    const response = await api.get('/super-admin/compliance-audit-logs/export', { params, responseType: 'blob' });
    return response;
  },
  verifyAuditLogIntegrity: async (limit = 1000) => {
    const response = await api.get('/super-admin/compliance-audit-logs/verify-integrity', { params: { limit } });
    return response.data;
  },
  getAllUsers: async () => {
    const response = await api.get('/admin/users');
    return response.data;
  },
  getAllCompanies: async () => {
    const response = await api.get('/admin/companies');
    return response.data;
  },
  getAllOrders: async () => {
    const response = await api.get('/admin/orders');
    return response.data;
  },
  // Discount APIs
  applyDiscount: async (orderId, discountPercentage, reason = null) => {
    const params = new URLSearchParams();
    params.append('discount_percentage', discountPercentage);
    if (reason) params.append('reason', reason);
    const response = await api.post(`/super-admin/orders/${orderId}/discount?${params.toString()}`);
    return response.data;
  },
  removeDiscount: async (orderId) => {
    const response = await api.delete(`/super-admin/orders/${orderId}/discount`);
    return response.data;
  },
  // Revenue APIs
  getRevenueSummary: async () => {
    const response = await api.get('/super-admin/revenue/summary');
    return response.data;
  },
  getRevenueByMonth: async (months = 12) => {
    const response = await api.get('/super-admin/revenue/by-month', { params: { months } });
    return response.data;
  },
  getRevenueByQuarter: async (year = null) => {
    const response = await api.get('/super-admin/revenue/by-quarter', { params: year ? { year } : {} });
    return response.data;
  },
  getRevenueByProduct: async () => {
    const response = await api.get('/super-admin/revenue/by-product');
    return response.data;
  },
  getRevenueByUser: async () => {
    const response = await api.get('/super-admin/revenue/by-user');
    return response.data;
  },
  getRevenueByCompany: async () => {
    const response = await api.get('/super-admin/revenue/by-company');
    return response.data;
  },
  getRevenueOrders: async (limit = 50, skip = 0) => {
    const response = await api.get('/super-admin/revenue/orders', { params: { limit, skip } });
    return response.data;
  },
  getSupportFooter: async () => {
    const response = await api.get('/super-admin/support-footer');
    return response.data;
  },
  updateSupportFooter: async (data) => {
    const response = await api.put('/super-admin/support-footer', data);
    return response.data;
  },
};

// Discount Modal Component
const DiscountModal = ({ order, onConfirm, onClose, loading }) => {
  const [discountPercentage, setDiscountPercentage] = useState(order?.discount_percentage || 0);
  const [reason, setReason] = useState(order?.discount_reason || '');
  
  const platformFees = order?.startease_fees || 0;
  const statutoryFees = order?.statutory_fees || 0;
  const quantity = order?.quantity || 1;
  const totalPlatformFees = platformFees * quantity;
  const discountAmount = (totalPlatformFees * discountPercentage) / 100;
  const totalAmount = totalPlatformFees + statutoryFees;
  const finalAmount = totalAmount - discountAmount;
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-lg w-full">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/20">
              <Tag className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Apply Discount</h2>
              <p className="text-sm text-slate-400">Order #{order?.id?.slice(0, 8)}</p>
            </div>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Order Info */}
          <div className="bg-slate-800/50 rounded-lg p-4">
            <p className="text-white font-medium">{order?.product?.name || 'Product'}</p>
            <p className="text-sm text-slate-400">{order?.company?.company_name || 'Company'}</p>
          </div>
          
          {/* Discount Slider */}
          <div>
            <label className="text-sm font-medium text-slate-400 mb-2 block">
              Discount Percentage
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="0"
                max="100"
                value={discountPercentage}
                onChange={(e) => setDiscountPercentage(Number(e.target.value))}
                className="flex-1 h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
              />
              <div className="flex items-center gap-1 bg-slate-800 rounded-lg px-3 py-2 w-24">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={discountPercentage}
                  onChange={(e) => setDiscountPercentage(Math.min(100, Math.max(0, Number(e.target.value))))}
                  className="bg-transparent text-white w-12 text-center outline-none"
                />
                <Percent className="w-4 h-4 text-slate-400" />
              </div>
            </div>
          </div>
          
          {/* Pricing Breakdown */}
          <div className="bg-slate-800/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Platform Fees</span>
              <span className="text-white">₹{totalPlatformFees.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Statutory Fees</span>
              <span className="text-white">₹{statutoryFees.toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Subtotal</span>
              <span className="text-white">₹{totalAmount.toLocaleString('en-IN')}</span>
            </div>
            {discountPercentage > 0 && (
              <div className="flex justify-between text-sm text-emerald-400">
                <span>Discount ({discountPercentage}%)</span>
                <span>-₹{discountAmount.toLocaleString('en-IN')}</span>
              </div>
            )}
            <div className="border-t border-slate-600 pt-2 flex justify-between">
              <span className="font-medium text-white">Final Amount</span>
              <span className="font-bold text-emerald-400 text-lg">₹{finalAmount.toLocaleString('en-IN')}</span>
            </div>
          </div>
          
          {/* Reason */}
          <div>
            <label className="text-sm font-medium text-slate-400 mb-2 block">Reason (Optional)</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for discount..."
              className="bg-slate-800 border-slate-700"
            />
          </div>
        </div>
        
        <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={() => onConfirm(discountPercentage, reason)}
            disabled={loading}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Tag className="w-4 h-4 mr-2" />}
            {order?.discount_percentage > 0 ? 'Update Discount' : 'Apply Discount'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Delete Preview Modal
const DeletePreviewModal = ({ user, preview, onConfirm, onClose, loading }) => {
  const [reason, setReason] = useState('');
  
  if (!preview) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/20">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Confirm User Deletion</h2>
              <p className="text-sm text-slate-400">This action cannot be undone</p>
            </div>
          </div>
        </div>
        
        <div className="p-6 space-y-6">
          {/* User Info */}
          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-slate-400 mb-2">User to Delete</h3>
            <p className="text-white font-medium">{preview.user?.full_name || 'Unknown'}</p>
            <p className="text-sm text-slate-400">{preview.user?.email}</p>
            <p className="text-xs text-slate-500 mt-1">Role: {preview.user?.role}</p>
          </div>
          
          {/* Impact Summary */}
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
            <h3 className="text-sm font-medium text-red-400 mb-3">Data to be Deleted</h3>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(preview.will_be_deleted || {}).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between bg-slate-800/50 rounded px-3 py-2">
                  <span className="text-sm text-slate-300 capitalize">{key.replace(/_/g, ' ')}</span>
                  <span className="text-sm font-medium text-red-400">{value}</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* Company Breakdown */}
          {preview.company_breakdown?.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-slate-400 mb-2">Companies to be Deleted</h3>
              <div className="space-y-2">
                {preview.company_breakdown.map((company, idx) => (
                  <div key={idx} className="bg-slate-800/50 rounded-lg p-3">
                    <p className="text-white font-medium">{company.name}</p>
                    <div className="flex gap-4 mt-1 text-xs text-slate-400">
                      <span>Directors: {company.directors}</span>
                      <span>Shareholders: {company.shareholders}</span>
                      <span>Orders: {company.orders}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {/* Reason Input */}
          <div>
            <label className="text-sm font-medium text-slate-400 mb-2 block">Reason for Deletion (Optional)</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason for audit log..."
              className="bg-slate-800 border-slate-700"
            />
          </div>
        </div>
        
        <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="destructive" 
            onClick={() => onConfirm(reason)}
            disabled={loading}
            className="bg-red-600 hover:bg-red-700"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Delete User & All Data
          </Button>
        </div>
      </div>
    </div>
  );
};

// Reassign Company Modal
const ReassignCompanyModal = ({ company, users, onConfirm, onClose, loading }) => {
  const [selectedUserId, setSelectedUserId] = useState('');
  const [reason, setReason] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const filteredUsers = users.filter(u => 
    u.id !== company?.user?.id &&
    (u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     u.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-lg w-full">
        <div className="p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-500/20">
              <ArrowRightLeft className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Reassign Company</h2>
              <p className="text-sm text-slate-400">Transfer ownership to another user</p>
            </div>
          </div>
        </div>
        
        <div className="p-6 space-y-4">
          {/* Company Info */}
          <div className="bg-slate-800/50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-slate-400 mb-1">Company</h3>
            <p className="text-white font-medium">{company?.company_name}</p>
            <p className="text-xs text-slate-500 mt-1">
              Current Owner: {company?.user?.full_name || company?.user?.email || 'Unknown'}
            </p>
          </div>
          
          {/* User Search */}
          <div>
            <label className="text-sm font-medium text-slate-400 mb-2 block">Select New Owner</label>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users..."
                className="pl-10 bg-slate-800 border-slate-700"
              />
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1 bg-slate-800/50 rounded-lg p-2">
              {filteredUsers.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No users found</p>
              ) : (
                filteredUsers.map(user => (
                  <button
                    key={user.id}
                    onClick={() => setSelectedUserId(user.id)}
                    className={`w-full text-left p-3 rounded-lg transition-colors ${
                      selectedUserId === user.id 
                        ? 'bg-indigo-600 text-white' 
                        : 'hover:bg-slate-700 text-slate-300'
                    }`}
                  >
                    <p className="font-medium">{user.full_name || 'Unknown'}</p>
                    <p className="text-xs opacity-70">{user.email}</p>
                  </button>
                ))
              )}
            </div>
          </div>
          
          {/* Reason */}
          <div>
            <label className="text-sm font-medium text-slate-400 mb-2 block">Reason (Optional)</label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for reassignment..."
              className="bg-slate-800 border-slate-700"
            />
          </div>
        </div>
        
        <div className="p-6 border-t border-slate-700 flex justify-end gap-3">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={() => onConfirm(selectedUserId, reason)}
            disabled={loading || !selectedUserId}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRightLeft className="w-4 h-4 mr-2" />}
            Reassign Company
          </Button>
        </div>
      </div>
    </div>
  );
};

// Audit Log Item
const AuditLogItem = ({ log }) => {
  const [expanded, setExpanded] = useState(false);
  
  const getActionIcon = () => {
    switch (log.action) {
      case 'user_deleted': return <UserX className="w-4 h-4 text-red-400" />;
      case 'company_deleted': return <Building2 className="w-4 h-4 text-red-400" />;
      case 'company_reassigned': return <ArrowRightLeft className="w-4 h-4 text-indigo-400" />;
      default: return <FileText className="w-4 h-4 text-slate-400" />;
    }
  };
  
  const getActionColor = () => {
    switch (log.action) {
      case 'user_deleted':
      case 'company_deleted':
      case 'bulk_delete':
        return 'bg-red-500/20 text-red-400';
      case 'company_reassigned':
        return 'bg-indigo-500/20 text-indigo-400';
      default:
        return 'bg-slate-500/20 text-slate-400';
    }
  };
  
  return (
    <div className="bg-slate-800/50 border border-slate-700/30 rounded-lg overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center gap-4 text-left hover:bg-slate-700/30 transition-colors"
      >
        <div className={`p-2 rounded-lg ${getActionColor()}`}>
          {getActionIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-medium truncate">{log.description}</p>
          <p className="text-xs text-slate-500">
            By {log.performed_by_email} • {toIST(log.created_at)}
          </p>
        </div>
        <span className={`px-2 py-1 rounded text-xs ${getActionColor()}`}>
          {log.action?.replace(/_/g, ' ')}
        </span>
        {expanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
      </button>
      
      {expanded && (
        <div className="p-4 pt-0 border-t border-slate-700/30 bg-slate-900/50">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500 mb-1">Target</p>
              <p className="text-white">{log.target_entity_type}: {log.target_entity_name || log.target_entity_id}</p>
            </div>
            <div>
              <p className="text-slate-500 mb-1">Performed By</p>
              <p className="text-white">{log.performed_by_email}</p>
              <p className="text-xs text-slate-500">{log.performed_by_role}</p>
            </div>
          </div>
          
          {log.records_affected && Object.keys(log.records_affected).length > 0 && (
            <div className="mt-4">
              <p className="text-slate-500 mb-2 text-sm">Records Affected</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(log.records_affected).map(([key, value]) => (
                  <span key={key} className="px-2 py-1 bg-slate-800 rounded text-xs text-slate-300">
                    {key.replace(/_/g, ' ')}: {value}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {log.from_user_id && log.to_user_id && (
            <div className="mt-4 p-3 bg-indigo-500/10 rounded-lg">
              <p className="text-xs text-indigo-400">
                Ownership transferred from user {log.from_user_id.slice(0, 8)}... to {log.to_user_id.slice(0, 8)}...
              </p>
            </div>
          )}
          
          {log.metadata?.reason && (
            <div className="mt-4">
              <p className="text-slate-500 mb-1 text-sm">Reason</p>
              <p className="text-white text-sm">{log.metadata.reason}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Activity Logs Section Component - Comprehensive Audit Trail
const ActivityLogsSection = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    action_type: '',
    entity_type: '',
    email: '',
    role: '',
    status: '',
    ip_address: '',
    start_date: '',
    end_date: '',
    failed_only: false
  });
  const [pagination, setPagination] = useState({ skip: 0, limit: 50, total: 0 });
  const [viewMode, setViewMode] = useState('logs'); // 'logs' | 'summary' | 'file_access' | 'login_sessions' | 'compliance'
  const [fileAccessLogs, setFileAccessLogs] = useState([]);
  const [loginSessions, setLoginSessions] = useState([]);
  const [loginSessionsSummary, setLoginSessionsSummary] = useState(null);
  const [sessionFilters, setSessionFilters] = useState({
    event_type: '',
    device_type: '',
    role: '',
    email: ''
  });
  // Compliance Audit state
  const [complianceLogs, setComplianceLogs] = useState([]);
  const [complianceSummary, setComplianceSummary] = useState(null);
  const [complianceEventTypes, setComplianceEventTypes] = useState(null);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [complianceFilters, setComplianceFilters] = useState({
    event_category: '',
    event_type: '',
    actor_email: '',
    actor_role: '',
    target_type: '',
    target_id: '',
    outcome: ''
  });
  const [verifyingIntegrity, setVerifyingIntegrity] = useState(false);
  const [integrityResult, setIntegrityResult] = useState(null);

  useEffect(() => {
    loadData();
  }, [viewMode, filters, pagination.skip, sessionFilters, complianceFilters]);

  useEffect(() => {
    // Load event types for compliance filter dropdowns
    if (viewMode === 'compliance' && !complianceEventTypes) {
      superAdminApi.getComplianceEventTypes().then(setComplianceEventTypes).catch(console.error);
    }
  }, [viewMode]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (viewMode === 'logs') {
        const cleanFilters = Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '' && v !== false)
        );
        const result = await superAdminApi.getActivityLogs({
          ...cleanFilters,
          skip: pagination.skip,
          limit: pagination.limit
        });
        setLogs(result.logs || []);
        setPagination(prev => ({ ...prev, total: result.total || 0 }));
      } else if (viewMode === 'summary') {
        const result = await superAdminApi.getActivityLogsSummary(30);
        setSummary(result);
      } else if (viewMode === 'file_access') {
        const result = await superAdminApi.getFileAccessLogs({
          skip: pagination.skip,
          limit: pagination.limit
        });
        setFileAccessLogs(result.logs || []);
        setPagination(prev => ({ ...prev, total: result.total || 0 }));
      } else if (viewMode === 'login_sessions') {
        const cleanSessionFilters = Object.fromEntries(
          Object.entries(sessionFilters).filter(([_, v]) => v !== '')
        );
        const [sessionsResult, summaryResult] = await Promise.all([
          superAdminApi.getLoginSessions({
            ...cleanSessionFilters,
            skip: pagination.skip,
            limit: pagination.limit
          }),
          superAdminApi.getLoginSessionsSummary(30)
        ]);
        setLoginSessions(sessionsResult.sessions || []);
        setLoginSessionsSummary(summaryResult);
        setPagination(prev => ({ ...prev, total: sessionsResult.total || 0 }));
      } else if (viewMode === 'compliance') {
        const cleanComplianceFilters = Object.fromEntries(
          Object.entries(complianceFilters).filter(([_, v]) => v !== '')
        );
        const [logsResult, summaryResult] = await Promise.all([
          superAdminApi.getComplianceAuditLogs({
            ...cleanComplianceFilters,
            skip: pagination.skip,
            limit: pagination.limit
          }),
          superAdminApi.getComplianceAuditSummary(30)
        ]);
        setComplianceLogs(logsResult.logs || []);
        setComplianceSummary(summaryResult);
        setPagination(prev => ({ ...prev, total: logsResult.total || 0 }));
      }
    } catch (error) {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const result = await superAdminApi.exportActivityLogs({ format, ...filters });
      if (format === 'csv' && result) {
        // Handle CSV download
        const blob = new Blob([result], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
      }
      toast.success('Export completed');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const ActionBadge = ({ action }) => {
    const colors = {
      LOGIN: 'bg-blue-500/20 text-blue-400',
      LOGOUT: 'bg-slate-500/20 text-slate-400',
      CREATE: 'bg-emerald-500/20 text-emerald-400',
      UPDATE: 'bg-amber-500/20 text-amber-400',
      DELETE: 'bg-red-500/20 text-red-400',
      READ: 'bg-purple-500/20 text-purple-400'
    };
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${colors[action] || 'bg-slate-500/20 text-slate-400'}`}>
        {action}
      </span>
    );
  };

  const StatusBadge = ({ status }) => (
    <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
      status === 'SUCCESS' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
    }`}>
      {status === 'SUCCESS' ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
      {status}
    </span>
  );

  return (
    <div className="space-y-4">
      {/* View Mode Selector */}
      <div className="flex gap-2 bg-slate-900/50 p-1 rounded-lg w-fit">
        {[
          { id: 'logs', label: 'Activity Logs' },
          { id: 'summary', label: 'Summary' },
          { id: 'file_access', label: 'File Access' },
          { id: 'login_sessions', label: 'Login Sessions' },
          { id: 'compliance', label: 'Compliance Audit' }
        ].map(mode => (
          <button
            key={mode.id}
            onClick={() => { setViewMode(mode.id); setPagination(prev => ({ ...prev, skip: 0 })); }}
            className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
              viewMode === mode.id ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
            }`}
          >
            {mode.label}
          </button>
        ))}
      </div>

      {/* Filters - Only for logs view */}
      {viewMode === 'logs' && (
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-400 uppercase">Filters</h3>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => handleExport('csv')} className="text-xs border-slate-600">
                Export CSV
              </Button>
              <Button size="sm" variant="outline" onClick={loadData} className="text-xs border-slate-600">
                <RefreshCw className="w-3 h-3 mr-1" /> Refresh
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <select
              value={filters.action_type}
              onChange={(e) => setFilters(prev => ({ ...prev, action_type: e.target.value }))}
              className="bg-slate-900 border border-slate-700 rounded-md p-2 text-sm text-white"
            >
              <option value="">All Actions</option>
              <option value="LOGIN">Login</option>
              <option value="LOGOUT">Logout</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
              <option value="READ">Read</option>
            </select>
            <select
              value={filters.entity_type}
              onChange={(e) => setFilters(prev => ({ ...prev, entity_type: e.target.value }))}
              className="bg-slate-900 border border-slate-700 rounded-md p-2 text-sm text-white"
            >
              <option value="">All Entities</option>
              <option value="order">Order</option>
              <option value="company">Company</option>
              <option value="director">Director</option>
              <option value="shareholder">Shareholder</option>
              <option value="user">User</option>
              <option value="file">File</option>
            </select>
            <select
              value={filters.role}
              onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
              className="bg-slate-900 border border-slate-700 rounded-md p-2 text-sm text-white"
            >
              <option value="">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="client">Client</option>
            </select>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="bg-slate-900 border border-slate-700 rounded-md p-2 text-sm text-white"
            >
              <option value="">All Status</option>
              <option value="SUCCESS">Success</option>
              <option value="FAILED">Failed</option>
            </select>
            <Input
              type="text"
              placeholder="Email (partial)"
              value={filters.email}
              onChange={(e) => setFilters(prev => ({ ...prev, email: e.target.value }))}
              className="bg-slate-900 border-slate-700 text-sm"
            />
            <Input
              type="text"
              placeholder="IP Address"
              value={filters.ip_address}
              onChange={(e) => setFilters(prev => ({ ...prev, ip_address: e.target.value }))}
              className="bg-slate-900 border-slate-700 text-sm"
            />
            <Input
              type="date"
              placeholder="Start Date"
              value={filters.start_date}
              onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
              className="bg-slate-900 border-slate-700 text-sm"
            />
            <Input
              type="date"
              placeholder="End Date"
              value={filters.end_date}
              onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
              className="bg-slate-900 border-slate-700 text-sm"
            />
          </div>
          <div className="mt-3 flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
              <input
                type="checkbox"
                checked={filters.failed_only}
                onChange={(e) => setFilters(prev => ({ ...prev, failed_only: e.target.checked }))}
                className="rounded bg-slate-800 border-slate-600"
              />
              Show failed attempts only
            </label>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setFilters({ action_type: '', entity_type: '', email: '', role: '', status: '', ip_address: '', start_date: '', end_date: '', failed_only: false })}
              className="text-xs text-slate-400"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      ) : viewMode === 'summary' && summary ? (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
              <p className="text-slate-500 text-sm mb-1">Total Events</p>
              <p className="text-2xl font-bold text-white">{summary.total_events?.toLocaleString()}</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
              <p className="text-slate-500 text-sm mb-1">Active Users</p>
              <p className="text-2xl font-bold text-blue-400">{summary.unique_active_users}</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
              <p className="text-slate-500 text-sm mb-1">Failed Events</p>
              <p className="text-2xl font-bold text-red-400">{summary.failed_events}</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
              <p className="text-slate-500 text-sm mb-1">Success Rate</p>
              <p className="text-2xl font-bold text-emerald-400">{summary.success_rate}%</p>
            </div>
          </div>

          {/* Action Breakdown */}
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <h3 className="text-sm font-semibold text-slate-400 uppercase mb-3">Action Breakdown</h3>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {Object.entries(summary.action_breakdown || {}).map(([action, count]) => (
                <div key={action} className="bg-slate-900/50 rounded-lg p-3 text-center">
                  <ActionBadge action={action.toUpperCase()} />
                  <p className="text-lg font-bold text-white mt-2">{count}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Top Active Users */}
          {summary.top_active_users?.length > 0 && (
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
              <h3 className="text-sm font-semibold text-slate-400 uppercase mb-3">Top Active Users</h3>
              <div className="space-y-2">
                {summary.top_active_users.map((user, idx) => (
                  <div key={user.user_id} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <span className="text-slate-500 w-6">{idx + 1}.</span>
                      <span className="text-white">{user.full_name || user.email}</span>
                    </div>
                    <span className="text-slate-400">{user.activity_count} actions</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suspicious IPs */}
          {summary.suspicious_ips?.length > 0 && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-red-400 uppercase mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Suspicious IPs (5+ failed attempts)
              </h3>
              <div className="space-y-2">
                {summary.suspicious_ips.map(ip => (
                  <div key={ip.ip} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3">
                    <span className="text-white font-mono">{ip.ip}</span>
                    <span className="text-red-400">{ip.failed_count} failed attempts</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : viewMode === 'file_access' ? (
        <div className="space-y-2">
          {fileAccessLogs.length === 0 ? (
            <p className="text-center text-slate-500 py-10">No file access logs found</p>
          ) : (
            fileAccessLogs.map(log => (
              <div key={log.id} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      log.permission_granted ? 'bg-emerald-500/20' : 'bg-red-500/20'
                    }`}>
                      {log.permission_granted ? <CheckCircle className="w-5 h-5 text-emerald-400" /> : <XCircle className="w-5 h-5 text-red-400" />}
                    </div>
                    <div>
                      <p className="text-white font-medium">{log.action}</p>
                      <p className="text-slate-500 text-sm">{log.file_type} - {log.entity_type}</p>
                    </div>
                  </div>
                  <p className="text-slate-500 text-xs">
                    {toIST(log.created_at)}
                  </p>
                </div>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div>
                    <p className="text-slate-500">User</p>
                    <p className="text-white">{log.user_name || log.user_email}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Role</p>
                    <p className="text-white capitalize">{log.user_role?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">IP Address</p>
                    <p className="text-white font-mono">{log.ip_address || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">File</p>
                    <p className="text-white truncate">{log.file_name || log.file_url}</p>
                  </div>
                </div>
                {!log.permission_granted && log.denial_reason && (
                  <div className="mt-3 p-2 bg-red-500/10 rounded text-xs text-red-400">
                    Denied: {log.denial_reason}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      ) : viewMode === 'login_sessions' ? (
        <div className="space-y-4">
          {/* Login Sessions Summary Cards */}
          {loginSessionsSummary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                <p className="text-slate-500 text-xs uppercase">Total Logins (30d)</p>
                <p className="text-2xl font-bold text-white">{loginSessionsSummary.total_logins || 0}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                <p className="text-slate-500 text-xs uppercase">Today's Logins</p>
                <p className="text-2xl font-bold text-emerald-400">{loginSessionsSummary.today_logins || 0}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                <p className="text-slate-500 text-xs uppercase">Unique Users</p>
                <p className="text-2xl font-bold text-blue-400">{loginSessionsSummary.unique_users || 0}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                <p className="text-slate-500 text-xs uppercase">Mobile vs Desktop</p>
                <p className="text-lg font-bold text-white">
                  <span className="text-purple-400">{loginSessionsSummary.by_device?.mobile || 0}</span>
                  <span className="text-slate-500 mx-1">/</span>
                  <span className="text-cyan-400">{loginSessionsSummary.by_device?.desktop || 0}</span>
                </p>
              </div>
            </div>
          )}
          
          {/* Session Filters */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <select
                value={sessionFilters.event_type}
                onChange={(e) => setSessionFilters(prev => ({ ...prev, event_type: e.target.value }))}
                className="bg-slate-900 border border-slate-700 rounded-md p-2 text-sm text-white"
              >
                <option value="">All Events</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
              </select>
              <select
                value={sessionFilters.device_type}
                onChange={(e) => setSessionFilters(prev => ({ ...prev, device_type: e.target.value }))}
                className="bg-slate-900 border border-slate-700 rounded-md p-2 text-sm text-white"
              >
                <option value="">All Devices</option>
                <option value="desktop">Desktop</option>
                <option value="mobile">Mobile</option>
              </select>
              <select
                value={sessionFilters.role}
                onChange={(e) => setSessionFilters(prev => ({ ...prev, role: e.target.value }))}
                className="bg-slate-900 border border-slate-700 rounded-md p-2 text-sm text-white"
              >
                <option value="">All Roles</option>
                <option value="super_admin">Super Admin</option>
                <option value="admin">Admin</option>
                <option value="client">Client</option>
              </select>
              <input
                type="text"
                placeholder="Search by email..."
                value={sessionFilters.email}
                onChange={(e) => setSessionFilters(prev => ({ ...prev, email: e.target.value }))}
                className="bg-slate-900 border border-slate-700 rounded-md p-2 text-sm text-white"
              />
            </div>
          </div>
          
          {/* Login Sessions List */}
          <div className="space-y-2">
            {loginSessions.length === 0 ? (
              <p className="text-center text-slate-500 py-10">No login sessions found</p>
            ) : (
              loginSessions.map(session => (
                <div key={session.id} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        session.event_type === 'login' ? 'bg-emerald-500/20' : 'bg-amber-500/20'
                      }`}>
                        {session.event_type === 'login' ? (
                          <User className="w-5 h-5 text-emerald-400" />
                        ) : (
                          <ArrowRightLeft className="w-5 h-5 text-amber-400" />
                        )}
                      </div>
                      <div>
                        <p className="text-white font-medium uppercase">{session.event_type}</p>
                        <p className="text-slate-500 text-sm">{session.full_name || session.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-slate-500 text-xs">
                        {toIST(session.timestamp)}
                      </p>
                      <p className="text-xs font-mono text-slate-600 mt-1">
                        Session: {session.session_id}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                    <div>
                      <p className="text-slate-500">User ID</p>
                      <p className="text-white font-mono truncate">{session.user_id}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Role</p>
                      <p className={`font-medium capitalize ${
                        session.role === 'super_admin' ? 'text-purple-400' :
                        session.role === 'admin' ? 'text-blue-400' : 'text-slate-300'
                      }`}>{session.role?.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Device</p>
                      <p className={`font-medium capitalize ${
                        session.device_type === 'mobile' ? 'text-purple-400' : 'text-cyan-400'
                      }`}>{session.device_type}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Browser / OS</p>
                      <p className="text-white">{session.browser} / {session.os}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">IP Address</p>
                      <p className="text-white font-mono">{session.ip_address}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      ) : viewMode === 'compliance' ? (
        <div className="space-y-4">
          {/* Compliance Audit Header with Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-medium">SOC 2 / ISO 27001 Compliant</span>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  setVerifyingIntegrity(true);
                  try {
                    const result = await superAdminApi.verifyAuditLogIntegrity(1000);
                    setIntegrityResult(result);
                    if (result.integrity_status === 'PASS') {
                      toast.success(`Integrity verified: ${result.valid}/${result.checked} records valid`);
                    } else {
                      toast.error(`Integrity check failed: ${result.tampered} tampered records found`);
                    }
                  } catch (e) {
                    toast.error('Failed to verify integrity');
                  } finally {
                    setVerifyingIntegrity(false);
                  }
                }}
                disabled={verifyingIntegrity}
                className="border-slate-600 text-slate-300"
              >
                {verifyingIntegrity ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                Verify Integrity
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={async () => {
                  try {
                    const response = await superAdminApi.exportComplianceAuditLogs({ format: 'csv', days: 30 });
                    const blob = new Blob([response.data], { type: 'text/csv' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `audit_logs_${new Date().toISOString().slice(0,10)}.csv`;
                    a.click();
                    toast.success('Audit logs exported');
                  } catch (e) {
                    toast.error('Export failed');
                  }
                }}
                className="border-slate-600 text-slate-300"
              >
                <FileText className="w-4 h-4 mr-1" />
                Export CSV
              </Button>
            </div>
          </div>

          {/* Integrity Result Banner */}
          {integrityResult && (
            <div className={`p-3 rounded-lg ${integrityResult.integrity_status === 'PASS' ? 'bg-emerald-500/20 border border-emerald-500/50' : 'bg-red-500/20 border border-red-500/50'}`}>
              <div className="flex items-center gap-2">
                {integrityResult.integrity_status === 'PASS' ? (
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-400" />
                )}
                <span className={integrityResult.integrity_status === 'PASS' ? 'text-emerald-400' : 'text-red-400'}>
                  Integrity Check: {integrityResult.integrity_status} - {integrityResult.valid}/{integrityResult.checked} records valid
                </span>
              </div>
            </div>
          )}

          {/* Summary Cards */}
          {complianceSummary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                <p className="text-slate-500 text-xs uppercase">Total Events (30d)</p>
                <p className="text-2xl font-bold text-white">{complianceSummary.total_logs || 0}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                <p className="text-slate-500 text-xs uppercase">Today</p>
                <p className="text-2xl font-bold text-blue-400">{complianceSummary.today_count || 0}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                <p className="text-slate-500 text-xs uppercase">Success</p>
                <p className="text-2xl font-bold text-emerald-400">{complianceSummary.success_count || 0}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                <p className="text-slate-500 text-xs uppercase">Failures</p>
                <p className="text-2xl font-bold text-red-400">{complianceSummary.failure_count || 0}</p>
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                <p className="text-slate-500 text-xs uppercase">Retention</p>
                <p className="text-sm font-medium text-amber-400">12 months min</p>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <select
                value={complianceFilters.event_category}
                onChange={(e) => setComplianceFilters(prev => ({ ...prev, event_category: e.target.value }))}
                className="bg-slate-900 border border-slate-700 rounded-md p-2 text-sm text-white"
              >
                <option value="">All Categories</option>
                {complianceEventTypes?.categories?.map(cat => (
                  <option key={cat} value={cat}>{cat.replace('_', ' ')}</option>
                ))}
              </select>
              <select
                value={complianceFilters.event_type}
                onChange={(e) => setComplianceFilters(prev => ({ ...prev, event_type: e.target.value }))}
                className="bg-slate-900 border border-slate-700 rounded-md p-2 text-sm text-white"
              >
                <option value="">All Event Types</option>
                {complianceEventTypes?.event_types?.map(type => (
                  <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Actor email..."
                value={complianceFilters.actor_email}
                onChange={(e) => setComplianceFilters(prev => ({ ...prev, actor_email: e.target.value }))}
                className="bg-slate-900 border border-slate-700 rounded-md p-2 text-sm text-white"
              />
              <select
                value={complianceFilters.actor_role}
                onChange={(e) => setComplianceFilters(prev => ({ ...prev, actor_role: e.target.value }))}
                className="bg-slate-900 border border-slate-700 rounded-md p-2 text-sm text-white"
              >
                <option value="">All Roles</option>
                <option value="super_admin">Super Admin</option>
                <option value="admin">Admin</option>
                <option value="client">Client</option>
              </select>
              <select
                value={complianceFilters.target_type}
                onChange={(e) => setComplianceFilters(prev => ({ ...prev, target_type: e.target.value }))}
                className="bg-slate-900 border border-slate-700 rounded-md p-2 text-sm text-white"
              >
                <option value="">All Target Types</option>
                {complianceEventTypes?.target_types?.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <input
                type="text"
                placeholder="Target ID..."
                value={complianceFilters.target_id}
                onChange={(e) => setComplianceFilters(prev => ({ ...prev, target_id: e.target.value }))}
                className="bg-slate-900 border border-slate-700 rounded-md p-2 text-sm text-white"
              />
              <select
                value={complianceFilters.outcome}
                onChange={(e) => setComplianceFilters(prev => ({ ...prev, outcome: e.target.value }))}
                className="bg-slate-900 border border-slate-700 rounded-md p-2 text-sm text-white"
              >
                <option value="">All Outcomes</option>
                <option value="success">Success</option>
                <option value="failure">Failure</option>
              </select>
            </div>
          </div>

          {/* Compliance Logs Table */}
          <div className="space-y-2">
            {complianceLogs.length === 0 ? (
              <p className="text-center text-slate-500 py-10">No compliance audit logs found</p>
            ) : (
              complianceLogs.map(log => (
                <div key={log.log_id} className="bg-slate-800/50 rounded-lg border border-slate-700/50 overflow-hidden">
                  {/* Main Row - Click to Expand */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-slate-700/30 transition-colors"
                    onClick={() => setExpandedLogId(expandedLogId === log.log_id ? null : log.log_id)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${log.outcome === 'success' ? 'bg-emerald-400' : 'bg-red-400'}`} />
                        <span className={`text-xs px-2 py-1 rounded font-medium ${
                          log.event_category === 'authentication' ? 'bg-blue-500/20 text-blue-400' :
                          log.event_category === 'data_access' ? 'bg-purple-500/20 text-purple-400' :
                          log.event_category === 'data_change' ? 'bg-amber-500/20 text-amber-400' :
                          log.event_category === 'document' ? 'bg-cyan-500/20 text-cyan-400' :
                          log.event_category === 'admin_action' ? 'bg-rose-500/20 text-rose-400' :
                          'bg-slate-700 text-slate-300'
                        }`}>
                          {log.event_category?.replace('_', ' ')}
                        </span>
                        <span className="text-white font-medium">{log.event_type?.replace(/_/g, ' ')}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-slate-500 text-xs">
                          {toIST(log.timestamp)}
                        </span>
                        {expandedLogId === log.log_id ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                      <div>
                        <p className="text-slate-500">Actor</p>
                        <p className="text-white truncate">{log.actor_email || 'anonymous'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Role</p>
                        <p className={`capitalize ${
                          log.actor_role === 'super_admin' ? 'text-purple-400' :
                          log.actor_role === 'admin' ? 'text-blue-400' : 'text-slate-300'
                        }`}>{log.actor_role?.replace('_', ' ') || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Target</p>
                        <p className="text-white">{log.target_type || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Target ID</p>
                        <p className="text-white font-mono truncate">{log.target_id?.slice(0, 12) || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500">Outcome</p>
                        <p className={log.outcome === 'success' ? 'text-emerald-400' : 'text-red-400'}>
                          {log.outcome}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedLogId === log.log_id && (
                    <div className="border-t border-slate-700/50 p-4 bg-slate-900/50 space-y-4">
                      {/* Device & Location Info */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                        <div>
                          <p className="text-slate-500 mb-1">IP Address</p>
                          <p className="text-white font-mono">{log.ip_address || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 mb-1">Device</p>
                          <p className="text-white">{log.device_type || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 mb-1">Browser</p>
                          <p className="text-white">{log.browser || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 mb-1">OS</p>
                          <p className="text-white">{log.os || 'N/A'}</p>
                        </div>
                      </div>

                      {/* Session & Integrity */}
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <p className="text-slate-500 mb-1">Session ID</p>
                          <p className="text-white font-mono">{log.session_id || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 mb-1">Integrity Hash</p>
                          <p className="text-emerald-400 font-mono text-xs truncate">{log.integrity_hash?.slice(0, 32)}...</p>
                        </div>
                      </div>

                      {/* Failure Reason */}
                      {log.failure_reason && (
                        <div className="p-3 bg-red-500/10 rounded border border-red-500/30">
                          <p className="text-red-400 text-xs font-medium">Failure Reason:</p>
                          <p className="text-red-300 text-sm mt-1">{log.failure_reason}</p>
                        </div>
                      )}

                      {/* Old/New Values */}
                      {(log.old_value || log.new_value) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {log.old_value && (
                            <div className="p-3 bg-red-500/10 rounded border border-red-500/30">
                              <p className="text-red-400 text-xs font-medium mb-2">Old Value:</p>
                              <pre className="text-slate-300 text-xs overflow-auto max-h-32">
                                {JSON.stringify(log.old_value, null, 2)}
                              </pre>
                            </div>
                          )}
                          {log.new_value && (
                            <div className="p-3 bg-emerald-500/10 rounded border border-emerald-500/30">
                              <p className="text-emerald-400 text-xs font-medium mb-2">New Value:</p>
                              <pre className="text-slate-300 text-xs overflow-auto max-h-32">
                                {JSON.stringify(log.new_value, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Metadata */}
                      {log.metadata && Object.keys(log.metadata).length > 0 && (
                        <div className="p-3 bg-slate-800 rounded border border-slate-700">
                          <p className="text-slate-400 text-xs font-medium mb-2">Additional Metadata:</p>
                          <pre className="text-slate-300 text-xs overflow-auto max-h-32">
                            {JSON.stringify(log.metadata, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Retention Notice */}
          <div className="text-center text-slate-500 text-xs py-2">
            Audit logs are retained for a minimum of 12 months in compliance with SOC 2 and ISO 27001 requirements.
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {logs.length === 0 ? (
            <p className="text-center text-slate-500 py-10">No activity logs found</p>
          ) : (
            logs.map(log => (
              <div key={log.id} className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <ActionBadge action={log.action_type} />
                    <StatusBadge status={log.status} />
                    {log.entity_type && (
                      <span className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-300">
                        {log.entity_type}
                      </span>
                    )}
                  </div>
                  <p className="text-slate-500 text-xs">
                    {toIST(log.timestamp)}
                  </p>
                </div>
                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div>
                    <p className="text-slate-500">User</p>
                    <p className="text-white">{log.full_name || log.email || 'Anonymous'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Role</p>
                    <p className="text-white capitalize">{log.role?.replace('_', ' ') || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">IP Address</p>
                    <p className="text-white font-mono">{log.ip_address || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Endpoint</p>
                    <p className="text-white truncate">{log.endpoint}</p>
                  </div>
                </div>
                {log.entity_id && (
                  <div className="mt-2 text-xs text-slate-500">
                    Entity ID: <span className="font-mono text-slate-400">{log.entity_id}</span>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination - For logs, file_access, login_sessions, and compliance views */}
      {(viewMode === 'logs' || viewMode === 'file_access' || viewMode === 'login_sessions' || viewMode === 'compliance') && pagination.total > pagination.limit && (
        <div className="flex items-center justify-between pt-4 border-t border-slate-700">
          <p className="text-sm text-slate-500">
            Showing {pagination.skip + 1} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={pagination.skip === 0}
              onClick={() => setPagination(prev => ({ ...prev, skip: Math.max(0, prev.skip - prev.limit) }))}
              className="border-slate-600"
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={pagination.skip + pagination.limit >= pagination.total}
              onClick={() => setPagination(prev => ({ ...prev, skip: prev.skip + prev.limit }))}
              className="border-slate-600"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Super Admin Tab Component
const SuperAdminTab = () => {
  const [activeSection, setActiveSection] = useState('revenue');
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [orders, setOrders] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Revenue states
  const [revenueSummary, setRevenueSummary] = useState(null);
  const [revenueByMonth, setRevenueByMonth] = useState([]);
  const [revenueByQuarter, setRevenueByQuarter] = useState([]);
  const [revenueByProduct, setRevenueByProduct] = useState([]);
  const [revenueByUser, setRevenueByUser] = useState([]);
  const [revenueByCompany, setRevenueByCompany] = useState([]);
  const [revenueView, setRevenueView] = useState('summary');
  
  // Modal states
  const [deletePreview, setDeletePreview] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reassignCompany, setReassignCompany] = useState(null);
  const [discountOrder, setDiscountOrder] = useState(null);
  
  // Support Footer state
  const [footerConfig, setFooterConfig] = useState(null);
  const [footerSaving, setFooterSaving] = useState(false);
  
  // Load data
  useEffect(() => {
    loadData();
  }, [activeSection]);
  
  const loadData = async () => {
    setLoading(true);
    try {
      if (activeSection === 'users') {
        const data = await superAdminApi.getAllUsers();
        setUsers(data);
      } else if (activeSection === 'companies') {
        const [companyData, userData] = await Promise.all([
          superAdminApi.getAllCompanies(),
          superAdminApi.getAllUsers(),
        ]);
        setCompanies(companyData);
        setUsers(userData);
      } else if (activeSection === 'discounts') {
        const data = await superAdminApi.getAllOrders();
        setOrders(data);
      } else if (activeSection === 'audit-logs') {
        const data = await superAdminApi.getAuditLogs({ limit: 100 });
        setAuditLogs(data.logs || []);
      } else if (activeSection === 'revenue') {
        // Load all revenue data
        const [summary, monthly, quarterly, byProduct, byUser, byCompany] = await Promise.all([
          superAdminApi.getRevenueSummary(),
          superAdminApi.getRevenueByMonth(12),
          superAdminApi.getRevenueByQuarter(),
          superAdminApi.getRevenueByProduct(),
          superAdminApi.getRevenueByUser(),
          superAdminApi.getRevenueByCompany()
        ]);
        setRevenueSummary(summary);
        setRevenueByMonth(monthly.monthly_revenue || []);
        setRevenueByQuarter(quarterly.quarterly_revenue || []);
        setRevenueByProduct(byProduct.product_revenue || []);
        setRevenueByUser(byUser.user_revenue || []);
        setRevenueByCompany(byCompany.company_revenue || []);
      } else if (activeSection === 'settings') {
        try {
          const cfg = await superAdminApi.getSupportFooter();
          setFooterConfig(cfg);
        } catch { setFooterConfig({ enabled: true, label: '', manager_name: '', manager_phone: '', hours: '' }); }
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle delete user
  const handlePreviewDelete = async (user) => {
    try {
      setActionLoading(true);
      const preview = await superAdminApi.previewDeleteUser(user.id);
      setDeletePreview(preview);
      setSelectedUser(user);
    } catch (error) {
      toast.error(getApiError(error, 'Failed to load preview'));
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleConfirmDelete = async (reason) => {
    try {
      setActionLoading(true);
      await superAdminApi.deleteUser(selectedUser.id, reason);
      toast.success(`User ${selectedUser.email} deleted successfully`);
      setDeletePreview(null);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to delete user'));
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle reassign company
  const handleReassignCompany = async (newOwnerId, reason) => {
    try {
      setActionLoading(true);
      await superAdminApi.reassignCompany(reassignCompany.id, newOwnerId, reason);
      toast.success(`Company reassigned successfully`);
      setReassignCompany(null);
      loadData();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to reassign company'));
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle delete company
  const handleDeleteCompany = async (company) => {
    if (!window.confirm(`Are you sure you want to delete "${company.company_name}" and ALL its data? This cannot be undone.`)) {
      return;
    }
    
    try {
      setActionLoading(true);
      await superAdminApi.deleteCompany(company.id);
      toast.success(`Company ${company.company_name} deleted successfully`);
      loadData();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to delete company'));
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle apply discount
  const handleApplyDiscount = async (discountPercentage, reason) => {
    try {
      setActionLoading(true);
      await superAdminApi.applyDiscount(discountOrder.id, discountPercentage, reason);
      toast.success(discountPercentage > 0 
        ? `${discountPercentage}% discount applied successfully` 
        : 'Discount removed successfully');
      setDiscountOrder(null);
      loadData();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to apply discount'));
    } finally {
      setActionLoading(false);
    }
  };
  
  // Handle remove discount
  const handleRemoveDiscount = async (order) => {
    if (!window.confirm('Remove discount from this order?')) return;
    
    try {
      setActionLoading(true);
      await superAdminApi.removeDiscount(order.id);
      toast.success('Discount removed successfully');
      loadData();
    } catch (error) {
      toast.error(getApiError(error, 'Failed to remove discount'));
    } finally {
      setActionLoading(false);
    }
  };
  
  // Filter functions
  const filteredUsers = users.filter(u => 
    u.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredCompanies = companies.filter(c =>
    c.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.cin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.user?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const filteredOrders = orders.filter(o =>
    o.product?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.company?.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.id?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  return (
    <div className="space-y-6">
      {/* Discount Modal */}
      {discountOrder && (
        <DiscountModal
          order={discountOrder}
          onConfirm={handleApplyDiscount}
          onClose={() => setDiscountOrder(null)}
          loading={actionLoading}
        />
      )}
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500/20 to-red-500/20">
            <Shield className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Super Admin</h1>
            <p className="text-sm text-slate-400">Revenue analytics, user management, discounts & audit logs</p>
          </div>
        </div>
      </div>
      
      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-slate-700 pb-2 overflow-x-auto">
        {[
          { id: 'revenue', label: 'Revenue Dashboard', icon: TrendingUp },
          { id: 'discounts', label: 'Discounts', icon: Tag },
          { id: 'users', label: 'User Management', icon: Users },
          { id: 'companies', label: 'Company Management', icon: Building2 },
          { id: 'audit-logs', label: 'Audit Logs', icon: Clock },
          { id: 'settings', label: 'Settings', icon: Shield },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveSection(tab.id); setSearchTerm(''); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeSection === tab.id
                ? 'bg-slate-800 text-white border-b-2 border-indigo-500'
                : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>
      
      {/* Search Bar */}
      {(activeSection === 'users' || activeSection === 'companies' || activeSection === 'discounts') && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={
              activeSection === 'users' ? 'Search users by name or email...' : 
              activeSection === 'discounts' ? 'Search orders by product, company, or ID...' :
              'Search companies...'
            }
            className="pl-10 bg-slate-800 border-slate-700"
          />
        </div>
      )}
      
      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
        </div>
      ) : (
        <>
          {/* Discounts Management Section */}
          {activeSection === 'discounts' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-slate-400 text-sm">{filteredOrders.length} orders found</p>
                <Button
                  onClick={loadData}
                  variant="ghost"
                  size="sm"
                  className="text-slate-400"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
              
              <div className="space-y-3">
                {filteredOrders.map(order => (
                  <div key={order.id} className="bg-slate-800/50 border border-slate-700/30 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <p className="text-white font-medium">{order.product?.name || 'Unknown Product'}</p>
                          {order.discount_percentage > 0 && (
                            <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full flex items-center gap-1">
                              <Tag className="w-3 h-3" />
                              {order.discount_percentage}% OFF
                            </span>
                          )}
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            order.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                            order.status === 'draft' ? 'bg-slate-500/20 text-slate-400' :
                            'bg-blue-500/20 text-blue-400'
                          }`}>
                            {order.status?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <p className="text-sm text-slate-400 mt-1">{order.company?.company_name || 'No company'}</p>
                        <p className="text-xs text-slate-500">Order #{order.id?.slice(0, 8)} • {order.user?.full_name || order.user?.email || 'Unknown user'}</p>
                        
                        {/* Pricing */}
                        <div className="flex items-center gap-6 mt-3 text-sm">
                          <div>
                            <span className="text-slate-500">Platform Fee:</span>
                            <span className="text-white ml-2">₹{(order.startease_fees || 0).toLocaleString('en-IN')}</span>
                          </div>
                          <div>
                            <span className="text-slate-500">Statutory:</span>
                            <span className="text-white ml-2">₹{(order.statutory_fees || 0).toLocaleString('en-IN')}</span>
                          </div>
                          {order.discount_percentage > 0 && (
                            <>
                              <div className="text-emerald-400">
                                <span>Discount:</span>
                                <span className="ml-2">-₹{(order.discount_amount || 0).toLocaleString('en-IN')}</span>
                              </div>
                              <div>
                                <span className="text-slate-500">Final:</span>
                                <span className="text-emerald-400 font-medium ml-2">
                                  ₹{(order.final_amount || order.total_amount || 0).toLocaleString('en-IN')}
                                </span>
                              </div>
                            </>
                          )}
                          {!order.discount_percentage && (
                            <div>
                              <span className="text-slate-500">Total:</span>
                              <span className="text-white font-medium ml-2">
                                ₹{(order.total_amount || ((order.startease_fees || 0) + (order.statutory_fees || 0))).toLocaleString('en-IN')}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => setDiscountOrder(order)}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          <Tag className="w-4 h-4 mr-1" />
                          {order.discount_percentage > 0 ? 'Edit' : 'Apply'} Discount
                        </Button>
                        {order.discount_percentage > 0 && (
                          <Button
                            onClick={() => handleRemoveDiscount(order)}
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {filteredOrders.length === 0 && (
                  <div className="text-center py-12 text-slate-400">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No orders found</p>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Revenue Dashboard Section */}
          {activeSection === 'revenue' && (
            <div className="space-y-6">
              {/* Revenue Summary Cards */}
              {revenueSummary && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <IndianRupee className="w-5 h-5 text-emerald-400" />
                      <span className="text-sm text-emerald-400">Total Revenue</span>
                    </div>
                    <p className="text-2xl font-bold text-white">₹{revenueSummary.total_revenue?.toLocaleString('en-IN') || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">Platform fees only</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <Calendar className="w-5 h-5 text-blue-400" />
                      <span className="text-sm text-blue-400">This Month</span>
                    </div>
                    <p className="text-2xl font-bold text-white">₹{revenueSummary.this_month_revenue?.toLocaleString('en-IN') || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">{revenueSummary.this_month_orders || 0} orders</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <ShoppingCart className="w-5 h-5 text-purple-400" />
                      <span className="text-sm text-purple-400">Total Orders</span>
                    </div>
                    <p className="text-2xl font-bold text-white">{revenueSummary.total_orders || 0}</p>
                    <p className="text-xs text-slate-400 mt-1">Paid orders</p>
                  </div>
                  <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-2">
                      <TrendingUp className="w-5 h-5 text-amber-400" />
                      <span className="text-sm text-amber-400">Avg Order Value</span>
                    </div>
                    <p className="text-2xl font-bold text-white">₹{Math.round(revenueSummary.average_order_value || 0).toLocaleString('en-IN')}</p>
                    <p className="text-xs text-slate-400 mt-1">Platform fee per order</p>
                  </div>
                </div>
              )}
              
              {/* Revenue View Selector */}
              <div className="flex gap-2 flex-wrap">
                {[
                  { id: 'summary', label: 'Monthly', icon: Calendar },
                  { id: 'quarter', label: 'Quarterly', icon: BarChart3 },
                  { id: 'product', label: 'By Product', icon: PieChart },
                  { id: 'user', label: 'By User', icon: Users },
                  { id: 'company', label: 'By Company', icon: Building2 },
                ].map(view => (
                  <button
                    key={view.id}
                    onClick={() => setRevenueView(view.id)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      revenueView === view.id
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                    }`}
                  >
                    <view.icon className="w-4 h-4" />
                    {view.label}
                  </button>
                ))}
              </div>
              
              {/* Monthly Revenue */}
              {revenueView === 'summary' && (
                <div className="bg-slate-800/50 border border-slate-700/30 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Monthly Revenue (Last 12 Months)</h3>
                  <div className="space-y-3">
                    {revenueByMonth.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No revenue data</p>
                    ) : (
                      revenueByMonth.map((month, idx) => (
                        <div key={idx} className="flex items-center gap-4">
                          <div className="w-28 text-sm text-slate-400">{month.month_name}</div>
                          <div className="flex-1 bg-slate-900 rounded-full h-6 overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full flex items-center justify-end pr-2"
                              style={{ width: `${Math.max(5, (month.revenue / Math.max(...revenueByMonth.map(m => m.revenue), 1)) * 100)}%` }}
                            >
                              <span className="text-xs text-white font-medium">₹{month.revenue.toLocaleString('en-IN')}</span>
                            </div>
                          </div>
                          <div className="w-20 text-right text-xs text-slate-500">{month.orders} orders</div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              
              {/* Quarterly Revenue */}
              {revenueView === 'quarter' && (
                <div className="bg-slate-800/50 border border-slate-700/30 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Quarterly Revenue ({new Date().getFullYear()})</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {revenueByQuarter.map((q, idx) => (
                      <div key={idx} className="bg-slate-900/50 rounded-lg p-4 text-center">
                        <p className="text-2xl font-bold text-indigo-400">{q.quarter}</p>
                        <p className="text-xs text-slate-500 mb-2">{q.months}</p>
                        <p className="text-lg font-semibold text-white">₹{q.revenue.toLocaleString('en-IN')}</p>
                        <p className="text-xs text-slate-400">{q.orders} orders</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Product-wise Revenue */}
              {revenueView === 'product' && (
                <div className="bg-slate-800/50 border border-slate-700/30 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Revenue by Product</h3>
                  <div className="space-y-3">
                    {revenueByProduct.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No product data</p>
                    ) : (
                      revenueByProduct.slice(0, 15).map((product, idx) => (
                        <div key={idx} className="flex items-center gap-4 bg-slate-900/50 rounded-lg p-3">
                          <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center text-indigo-400 font-bold text-sm">
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{product.product_name}</p>
                            <p className="text-xs text-slate-500">{product.category} • {product.orders} orders</p>
                          </div>
                          <div className="text-right">
                            <p className="text-emerald-400 font-semibold">₹{product.revenue.toLocaleString('en-IN')}</p>
                            <p className="text-xs text-slate-500">Avg: ₹{Math.round(product.average_per_order).toLocaleString('en-IN')}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              
              {/* User-wise Revenue */}
              {revenueView === 'user' && (
                <div className="bg-slate-800/50 border border-slate-700/30 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Revenue by User</h3>
                  <div className="space-y-3">
                    {revenueByUser.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No user data</p>
                    ) : (
                      revenueByUser.slice(0, 20).map((user, idx) => (
                        <div key={idx} className="flex items-center gap-4 bg-slate-900/50 rounded-lg p-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white font-medium">
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{user.name || 'Unknown'}</p>
                            <p className="text-xs text-slate-500 truncate">{user.email}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-emerald-400 font-semibold">₹{user.revenue.toLocaleString('en-IN')}</p>
                            <p className="text-xs text-slate-500">{user.orders} orders</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              
              {/* Company-wise Revenue */}
              {revenueView === 'company' && (
                <div className="bg-slate-800/50 border border-slate-700/30 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Revenue by Company</h3>
                  <div className="space-y-3">
                    {revenueByCompany.length === 0 ? (
                      <p className="text-slate-500 text-center py-4">No company data</p>
                    ) : (
                      revenueByCompany.slice(0, 20).map((company, idx) => (
                        <div key={idx} className="flex items-center gap-4 bg-slate-900/50 rounded-lg p-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-blue-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium truncate">{company.company_name}</p>
                            <p className="text-xs text-slate-500 truncate">Owner: {company.owner_email}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-emerald-400 font-semibold">₹{company.revenue.toLocaleString('en-IN')}</p>
                            <p className="text-xs text-slate-500">{company.orders} orders</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Users Section */}
          {activeSection === 'users' && (
            <div className="space-y-3">
              {filteredUsers.length === 0 ? (
                <p className="text-center text-slate-500 py-10">No users found</p>
              ) : (
                filteredUsers.map(user => (
                  <div
                    key={user.id}
                    className="bg-slate-800/50 border border-slate-700/30 rounded-lg p-4 flex items-center gap-4"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-medium">
                      {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium">{user.full_name || 'Unknown'}</p>
                      <p className="text-sm text-slate-400">{user.email}</p>
                      <div className="flex gap-2 mt-1">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          user.role === 'super_admin' ? 'bg-amber-500/20 text-amber-400' :
                          user.role === 'admin' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-slate-500/20 text-slate-400'
                        }`}>
                          {user.role}
                        </span>
                        <span className="text-xs text-slate-500">{user.company_count || 0} companies</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePreviewDelete(user)}
                        disabled={user.role === 'super_admin' || actionLoading}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        title={user.role === 'super_admin' ? 'Cannot delete super admin' : 'Delete user'}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          
          {/* Companies Section */}
          {activeSection === 'companies' && (
            <div className="space-y-3">
              {filteredCompanies.length === 0 ? (
                <p className="text-center text-slate-500 py-10">No companies found</p>
              ) : (
                filteredCompanies.map(company => (
                  <div
                    key={company.id}
                    className="bg-slate-800/50 border border-slate-700/30 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium">{company.company_name}</p>
                        <p className="text-xs text-slate-500">{company.cin || 'No CIN'}</p>
                        <div className="flex gap-4 mt-2 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {company.user?.full_name || company.user?.email || 'Unknown owner'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {company.directors_count || 0} directors
                          </span>
                          <span className="flex items-center gap-1">
                            <ShoppingCart className="w-3 h-3" />
                            {company.order_count || 0} orders
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setReassignCompany(company)}
                          disabled={actionLoading}
                          className="text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10"
                          title="Reassign to another user"
                        >
                          <ArrowRightLeft className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCompany(company)}
                          disabled={actionLoading}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          title="Delete company"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          
          {/* Audit Logs Section - Enhanced Activity Logs */}
          {activeSection === 'audit-logs' && (
            <ActivityLogsSection />
          )}

          {/* Settings Section */}
          {activeSection === 'settings' && footerConfig && (
            <div className="space-y-6" data-testid="settings-section">
              <div className="admin-card" style={{ padding: 24 }}>
                <h2 style={{ fontFamily: "'Lora', serif", fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Support Footer</h2>
                <p style={{ fontSize: 13, color: 'var(--admin-text-dim)', marginBottom: 20 }}>
                  This footer appears on all client-facing emails. Changes are applied immediately.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Enabled Toggle */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 14, background: 'var(--admin-bg)', borderRadius: 8 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>Footer Enabled</div>
                      <div style={{ fontSize: 12, color: 'var(--admin-text-soft)' }}>Show support contact in client emails</div>
                    </div>
                    <button
                      data-testid="footer-enabled-toggle"
                      onClick={() => setFooterConfig(c => ({ ...c, enabled: !c.enabled }))}
                      style={{
                        width: 44, height: 24, borderRadius: 12, border: 'none', cursor: 'pointer',
                        background: footerConfig.enabled ? 'var(--admin-green)' : 'var(--admin-bg-hover)',
                        position: 'relative', transition: 'background 200ms',
                      }}
                    >
                      <div style={{
                        width: 18, height: 18, borderRadius: '50%', background: '#fff',
                        position: 'absolute', top: 3,
                        left: footerConfig.enabled ? 23 : 3,
                        transition: 'left 200ms',
                      }} />
                    </button>
                  </div>

                  {/* Label */}
                  <div>
                    <div className="admin-input-label">Role / Label</div>
                    <input
                      className="admin-input"
                      value={footerConfig.label || ''}
                      onChange={e => setFooterConfig(c => ({ ...c, label: e.target.value }))}
                      placeholder="e.g. Dashboard Manager"
                      data-testid="footer-label-input"
                    />
                  </div>

                  {/* Name */}
                  <div>
                    <div className="admin-input-label">Manager Name</div>
                    <input
                      className="admin-input"
                      value={footerConfig.manager_name || ''}
                      onChange={e => setFooterConfig(c => ({ ...c, manager_name: e.target.value }))}
                      placeholder="e.g. Sadhavi Yadav"
                      data-testid="footer-name-input"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <div className="admin-input-label">Phone Number (10 digits, no +91)</div>
                    <input
                      className="admin-input"
                      value={footerConfig.manager_phone || ''}
                      onChange={e => setFooterConfig(c => ({ ...c, manager_phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))}
                      placeholder="e.g. 7570021003"
                      data-testid="footer-phone-input"
                    />
                  </div>

                  {/* Hours */}
                  <div>
                    <div className="admin-input-label">Working Hours</div>
                    <input
                      className="admin-input"
                      value={footerConfig.hours || ''}
                      onChange={e => setFooterConfig(c => ({ ...c, hours: e.target.value }))}
                      placeholder="e.g. Monday to Friday, 12:30 PM to 6:30 PM IST"
                      data-testid="footer-hours-input"
                    />
                  </div>

                  {/* Preview */}
                  <div style={{ padding: 16, background: 'var(--admin-bg)', borderRadius: 8, border: '1px dashed var(--admin-border)' }}>
                    <div className="admin-input-label" style={{ marginBottom: 8 }}>Preview</div>
                    <div style={{ background: '#fff', borderRadius: 6, padding: 16, color: '#374151', fontSize: 13 }}>
                      <hr style={{ border: 'none', borderTop: '1px solid #d1d5db', margin: '0 0 12px' }} />
                      <p style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 14 }}>Need help?</p>
                      <p style={{ margin: '0 0 8px', color: '#6b7280' }}>Reply to this email or contact our support team:</p>
                      <p style={{ margin: 0 }}>{footerConfig.label}: <strong>{footerConfig.manager_name}</strong></p>
                      <p style={{ margin: 0 }}>Phone: <a href={`tel:+91${footerConfig.manager_phone}`} style={{ color: '#2563eb' }}>+91 {footerConfig.manager_phone}</a></p>
                      <p style={{ margin: 0 }}>Hours: {footerConfig.hours}</p>
                      <hr style={{ border: 'none', borderTop: '1px solid #d1d5db', margin: '12px 0 0' }} />
                    </div>
                  </div>

                  {/* Save */}
                  <button
                    className="admin-btn admin-btn-primary"
                    disabled={footerSaving}
                    data-testid="footer-save-btn"
                    onClick={async () => {
                      setFooterSaving(true);
                      try {
                        await superAdminApi.updateSupportFooter({
                          enabled: footerConfig.enabled,
                          label: footerConfig.label,
                          manager_name: footerConfig.manager_name,
                          manager_phone: footerConfig.manager_phone,
                          hours: footerConfig.hours,
                        });
                        toast.success('Support footer updated — applies to all new emails immediately');
                      } catch (err) {
                        toast.error(getApiError(err, 'Failed to save'));
                      } finally { setFooterSaving(false); }
                    }}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    {footerSaving ? <Loader2 style={{ width: 14, height: 14, animation: 'spin 1s linear infinite' }} /> : <CheckCircle style={{ width: 14, height: 14 }} />}
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Delete Preview Modal */}
      {deletePreview && (
        <DeletePreviewModal
          user={selectedUser}
          preview={deletePreview}
          onConfirm={handleConfirmDelete}
          onClose={() => { setDeletePreview(null); setSelectedUser(null); }}
          loading={actionLoading}
        />
      )}
      
      {/* Reassign Company Modal */}
      {reassignCompany && (
        <ReassignCompanyModal
          company={reassignCompany}
          users={users}
          onConfirm={handleReassignCompany}
          onClose={() => setReassignCompany(null)}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default SuperAdminTab;
